import React, { useState, useRef, useEffect } from "react";
import { Search, Filter, Check } from "lucide-react";

const SectionFilter = ({ 
  searchTerm, 
  onSearchChange, 
  placeholder = "Search...", 
  columns = [],
  selectedColumn,
  onColumnSelect 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterableColumns = [
    { label: "None" },
    ...columns.filter((col) => col.label.toLowerCase() !== "actions")
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-4 relative w-full">
      <div className="relative w-full flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input 
          type="text"
          value={searchTerm}
          placeholder={selectedColumn === "None" ? placeholder : `Searching in ${selectedColumn}...`}
          className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-100 dark:border-white/5 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-medium text-stone-700 dark:text-stone-200"
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      
      <div className="relative w-full lg:w-auto" ref={dropdownRef}>
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center justify-center gap-2.5 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all border border-white/10 shadow-lg active:scale-95 bg-[#0F172A] text-white w-full lg:min-w-[140px] ${
            isOpen ? "shadow-slate-900/40" : "shadow-slate-900/20"
          }`}
        >
          <Filter size={14} className="text-white" /> 
          {selectedColumn === "None" ? "Sort By" : selectedColumn}
        </button>

        {isOpen && (
          <div className="absolute left-0 lg:left-auto lg:right-0 mt-3 w-full lg:w-52 bg-white dark:bg-slate-900 border border-stone-100 dark:border-white/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-stone-50 dark:border-white/5 bg-stone-50/50 dark:bg-white/5">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-stone-400 block text-center lg:text-left">
                Select Sort Category
              </span>
            </div>
            <div className="p-2 space-y-1">
              {filterableColumns.map((col, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onColumnSelect(col.label);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all group ${
                    selectedColumn === col.label 
                      ? "bg-blue-50 dark:bg-blue-900/20" 
                      : "hover:bg-stone-50 dark:hover:bg-white/5"
                  }`}
                >
                  <span className={`text-[11px] font-black uppercase tracking-tight ${
                    selectedColumn === col.label ? "text-blue-600 dark:text-blue-400" : "text-stone-600 dark:text-stone-300"
                  }`}>
                    {col.label}
                  </span>
                  
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedColumn === col.label 
                      ? "border-blue-600 bg-blue-600" 
                      : "border-stone-200 dark:border-stone-700 bg-transparent"
                  }`}>
                    {selectedColumn === col.label && (
                      <Check size={10} className="text-white" strokeWidth={4} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionFilter;